<script setup>
import { useI18n } from 'vue-i18n'
import CreateMapDrawer from '@/views/maps/CreateMapDrawer.vue'
import moment from "moment";
import axios from '@/helpers/axios'

definePage({
  meta: {
    layout: 'default',
    role: 'admin'
  },
})

const { t } = useI18n()

const showCreateMapDrawer = ref(false)

const searchQuery = ref('')
const rowPerPage = ref(10)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)
const items = ref([])

const getMaps = () => {
  axios.get('/admin/maps', {
    params: {
      q: searchQuery.value,
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
    },
  }).then(response => {
    items.value = response.data.data
    totalPage.value = response.data.meta.last_page
    totalItems.value = response.data.meta.total
  }).catch(e => {
    console.error(e)
  })
}

watchEffect(() => {
  getMaps()
})

watchEffect(() => {
  if (currentPage.value > totalPage.value)
    currentPage.value = totalPage.value
})


// 👉 Computing pagination data
const paginationData = computed(() => {
  const firstIndex = items.value.length ? (currentPage.value - 1) * rowPerPage.value + 1 : 0
  const lastIndex = items.value.length + (currentPage.value - 1) * rowPerPage.value
  
  return t('tables.showing', { firstIndex, lastIndex, totalItems: totalItems.value })
})

const resolveStatusColor = stat => {
  if (stat === false)
    return 'warning'
  
  return 'primary'
}

const resolveStatusText = stat => {
  if (stat === false)
    return t('maps.pending')
  
  return t('maps.processed')
}
</script>

<template>
  <div>
    <VCard
      v-if="items"
      id="items-list"
      :title="$t('maps.title') + ' 🗺️'"
    >
      <VCardText class="d-flex align-center flex-wrap gap-4">
        <!-- 👉 Rows per page -->
        <div
          class="d-flex align-center"
          style="width: 150px;"
        >
          <span class="text-no-wrap me-3">{{ $t('tables.show') }}</span>
          <VSelect
            v-model="rowPerPage"
            density="compact"
            :items="[10, 20, 30, 50]"
          />
        </div>

        <VSpacer />

        <div class="d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div class="items-list-filter">
            <VTextField
              v-model="searchQuery"
              :placeholder="$t('maps.search_map')"
              density="compact"
            />
          </div>
          <VBtn
            prepend-icon="tabler-plus"
            @click="showCreateMapDrawer = true"
          >
            {{ $t('maps.add_map') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION Table -->
      <VTable class="text-no-wrap items-list-table">
        <!-- 👉 Table head -->
        <thead class="text-uppercase">
          <tr>
            <th scope="col">
              {{ $t('maps.name') }}
            </th>

            <th scope="col">
              {{ $t('users.name') }}
            </th>

            <th scope="col">
              {{ $t('maps.area') }}
            </th>

            <th scope="col">
              {{ $t('maps.layers') }}
            </th>

            <th scope="col">
              {{ $t('maps.status') }}
            </th>

            <th scope="col">
              {{ $t('maps.sampled_at') }}
            </th>

            <th scope="col" />
          </tr>
        </thead>

        <!-- 👉 Table Body -->
        <tbody>
          <tr
            v-for="map in items"
            :key="map.uuid"
            style="height: 3.75rem;"
          >
            <!-- 👉 Name -->
            <td>
              <RouterLink :to="{ name: 'admin-maps-uuid', params: { uuid: map.uuid } }">
                {{ map.name }}
              </RouterLink>
            </td>

            <!-- 👉 User -->
            <td>
              <RouterLink
                v-if="map.user"
                :to="{ name: 'admin-users-uuid', params: { uuid: map.user.uuid } }"
              >
                {{ map.user.name }}
              </RouterLink>
              <span v-else>-</span>
            </td>

            <!-- 👉 Model -->
            <td>
              {{ map.area }}
            </td>

            <!-- 👉 Layers -->
            <td>
              {{ map.layers.length }}
            </td>

            <!-- 👉 Processed -->
            <td>
              <VChip
                :color="resolveStatusColor(map.processed)"
                label
                size="small"
                class="font-weight-medium"
              >
                {{ resolveStatusText(map.processed) }}
              </VChip>
            </td>

            <!-- 👉 Sampled -->
            <td>
              {{ moment.utc(map.sampled_at).local().format("Y-MM-DD HH:mm") }}
            </td>

            <!-- 👉 Actions -->
            <td style="width: 8rem;">
              <VTooltip :text="$t('layout.view')">
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    icon
                    variant="text"
                    color="default"
                    size="x-small"
                    :to="{ name: 'admin-maps-uuid', params: { uuid: map.uuid } }"
                  >
                    <VIcon
                      :size="22"
                      icon="tabler-eye"
                    />
                  </VBtn>
                </template>
              </VTooltip>
            </td>
          </tr>
        </tbody>

        <!-- 👉 table footer  -->
        <tfoot v-show="!items.length">
          <tr>
            <td
              colspan="8"
              class="text-center text-body-1"
            >
              {{ $t('maps.no_maps') }}
            </td>
          </tr>
        </tfoot>
      </VTable>
      <!-- !SECTION -->

      <VDivider />

      <!-- SECTION Pagination -->
      <VCardText class="d-flex align-center flex-wrap gap-4 py-3">
        <!-- 👉 Pagination meta -->
        <span class="text-sm text-disabled">
          {{ paginationData }}
        </span>

        <VSpacer />

        <!-- 👉 Pagination -->
        <VPagination
          v-model="currentPage"
          size="small"
          :total-visible="5"
          :length="totalPage"
        />
      </VCardText>
      <!-- !SECTION -->
    </VCard>
    <CreateMapDrawer
      :is-drawer-open="showCreateMapDrawer"
      @update:is-drawer-open="(val) => showCreateMapDrawer = val"
      @submit="getMaps"
    />
  </div>
</template>

<style lang="scss">
#items-list {
  .items-list-actions {
    inline-size: 8rem;
  }

  .items-list-filter {
    inline-size: 12rem;
  }
}
</style>
